import { useClientSize } from '@/app/hooks';
import { TipIcon } from '@/app/ui/iconComponents';
import apx from '@/landing/constants/adaptivepx';
import { BreakpointsEnum } from '@b2b/shared/types';
import { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

interface iTipProps {
  message: string;
  icon?: React.ReactNode;
}

const Tip: FC<iTipProps> = ({ message, icon }) => {
  const { getIsBreakpoint } = useClientSize();
  const isMobile = getIsBreakpoint('sm');

  const [isTipVisible, setIsTipVisible] = useState<boolean>(false);

  const handleInside = () => isMobile && setIsTipVisible(true);
  const handleOutside = () => setIsTipVisible(false);

  const divRef = useRef<HTMLDivElement | null>(null);

  function calculateTooltipPosition() {
    if (!divRef.current) {
      return;
    }

    const rect = divRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const targetLeftPosition = viewportWidth * 0.05;
    const currentLeftPosition = rect.left;
    const offset = currentLeftPosition - targetLeftPosition;

    if (offset > 0) {
      divRef.current.style.transform = `translateX(-${offset}px)`;
    }
  }

  useEffect(() => {
    if (!isTipVisible) {
      return;
    }

    calculateTooltipPosition();
    window.addEventListener('resize', calculateTooltipPosition);

    return () => {
      window.removeEventListener('resize', calculateTooltipPosition);
    };
  }, []);

  useEffect(() => {
    calculateTooltipPosition();
  }, [isTipVisible]);

  return (
    <Container>
      {isTipVisible && isMobile && <Message ref={divRef}>{message}</Message>}
      {isTipVisible && isMobile && (
        <img
          src="/assets/images/filters/tip.png"
          alt=""
        />
      )}

      <Wrapper
        title={message}
        onMouseEnter={handleInside}
        onMouseLeave={handleOutside}
        onClick={(event: React.MouseEvent<HTMLDivElement>) => event.preventDefault()}
      >
        {icon ? icon : <TipIcon />}
      </Wrapper>
    </Container>
  );
};

export default Tip;

const Container = styled.div`

  position: relative;
  display: block !important;
  width: ${apx.d(20)};
  height: ${apx.d(16)};
  padding-left: ${apx.d(4)};

  @media (max-width: ${BreakpointsEnum.md}px) {

    width: ${apx.d(26)};
    height: ${apx.d(26)};

  }

  @media (max-width: ${BreakpointsEnum.sm}px) {

    width: ${apx.m(20)};
    height: ${apx.m(16)};
    padding-left: ${apx.m(4)};

  }

  img {

    position: absolute;
    top: -50%;
    left: ${apx.d(5)};
    width: ${apx.d(14)};
    height: ${apx.d(7)};
    z-index: 11;

    @media (max-width: ${BreakpointsEnum.md}px) {

      left: ${apx.d(7)};
      width: ${apx.d(16)};
      height: ${apx.d(9)};

    }

    @media (max-width: ${BreakpointsEnum.sm}px) {

      left: ${apx.m(5)};
      width: ${apx.m(14)};
      height: ${apx.m(7)};

    }


  }

  svg {

    display: block !important;
    width: ${apx.d(16)};
    height: ${apx.d(16)};
    z-index: 10;
    overflow: visible;

    @media (max-width: ${BreakpointsEnum.md}px) {

      width: ${apx.d(26)};
      height: ${apx.d(26)};

    }

    @media (max-width: ${BreakpointsEnum.sm}px) {

      width: ${apx.m(16)};
      height: ${apx.m(16)};

    }

  }

`;

const Message = styled.div`

  position: absolute;
  bottom: ${apx.d(22)};
  left: -${apx.d(16)};
  padding: ${apx.d(6)} ${apx.d(8)};
  border-radius: ${apx.d(8)};
  background-color: #FFF;
  z-index: 10;
  box-shadow: 0 ${apx.d(10)} ${apx.d(30)} 0 #1F223D1A;
  color: #000;
  font-size: ${apx.d(15)};
  line-height: ${apx.d(18)};
  font-weight: 400;
  white-space: normal;
  overflow-wrap: break-word;

  min-width: 90vw;
  max-width: 90vw;

  @media (max-width: ${BreakpointsEnum.lg}px) {



  }

  @media (max-width: ${BreakpointsEnum.md}px) {


    bottom: ${apx.d(36)};
    left: -${apx.d(36)};
    font-size: ${apx.d(18)};
    line-height: ${apx.d(20)};

  }

  @media (max-width: ${BreakpointsEnum.sm}px) {

    bottom: ${apx.m(22)};

    padding: ${apx.m(6)} ${apx.m(8)};
    border-radius: ${apx.m(8)};
    box-shadow: 0 ${apx.m(10)} ${apx.m(30)} 0 #1F223D1A;
    font-size: ${apx.m(8)};
    line-height: ${apx.m(10)};

  }

  @media (max-width: ${BreakpointsEnum.s}px) {

    font-size: ${apx.m(12.2)};
    line-height: ${apx.m(13)};

  }

`;

const Wrapper = styled.div`

  width: ${apx.d(16)};
  height: ${apx.d(16)};

  @media (max-width: ${BreakpointsEnum.md}px) {

    width: ${apx.d(26)};
    height: ${apx.d(26)};

  }

  @media (max-width: ${BreakpointsEnum.sm}px) {

    width: ${apx.m(16)};
    height: ${apx.m(16)};

  }

`;
